import { useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
} from '@product-framework/index';
import {
  useDataProvider,
  Breadcrumb,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useLocalization,
  useSnackbar,
  useStore,
} from '@ui-framework/index';

import { useNavigate } from 'react-router-dom';
import { createSchema } from '../../schemas';
import validationSchema from './validations';

function CountryCreate() {
  const { formatMessage } = useLocalization();
  const createFormRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, get, getApiUrl } = useDataProvider(provider);
  const [formErrors, setFormErrors] = useState(null);
  const store = useStore();
  const storeName = 'countryFormStore';

  const postApiUrl = `${getApiUrl()}/sxcl/v1/countries`;

  const preProcessRequestData = data => {
    const { isActive, ...other } = data;
    return {
      isActive: isActive === 'true',
      ...other,
    };
  };

  const submitCreateForm = async data => {
    setFormErrors(null);
    const requestData = preProcessRequestData(data);
    store.uiState[storeName].set(requestData);
    const response = await post(postApiUrl, { ...requestData });
    if (response?.ok) {
      store.uiState[storeName].set({});
      snackbar.showMessage(
        formatMessage({
          id: 'COUNTRY_CREATE_SUCCESS',
          defaultMessage: 'Country was saved successfully',
        }),
      );
      createFormRef.current?.resetAndNavigate(
        `/countries/edit/${response?.data?.id}`,
      );
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'COUNTRY_CREATE_FAILURE',
          defaultMessage: 'Error: Could not save the country',
        }),
      });
    }
  };

  const formProps = {
    customComponents: FormFields,
    schema: createSchema,
    onSubmit: submitCreateForm,
    ref: createFormRef,
    initialValues: store.uiState[storeName].get(),
    validationSchema: validationSchema(formatMessage, getApiUrl, get),
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'COUNTRIES_BREADCRUMB',
        defaultMessage: 'Countries',
      }),
      icon: IconLibrary.Countries,
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/countries');
      },
    },
    {
      name: formatMessage({
        id: 'CREATE_COUNTRY_BREADCRUMB',
        defaultMessage: 'Create Country',
      }),
      icon: IconLibrary.Country,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'EDIT_COUNTRIES_STACKABLE_BUTTON',
        defaultMessage: 'List Countries',
      }),
      Icon: IconLibrary.Countries,
      variant: 'outlined',
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/countries');
      },
    },
  ];

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <PageSection
        label={formatMessage({
          id: 'COUNTRY_CREATE_HEADING',
          defaultMessage: 'Create Country',
        })}>
        <Box>
          <CRUDComponent
            errors={formErrors}
            type={CRUD_COMPONENT_TYPE.FORM}
            formProps={formProps}>
            <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
              <Button
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => createFormRef.current?.submit()}>
                {formatMessage({
                  id: 'COUNTRY_CREATE_SAVE_BUTTON',
                  defaultMessage: 'Save Country',
                })}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Reset />}
                onClick={() => createFormRef.current?.reset()}>
                {formatMessage({
                  id: 'COUNTRY_CREATE_RESET_BUTTON',
                  defaultMessage: 'Reset',
                })}
              </Button>
            </Box>
          </CRUDComponent>
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(CountryCreate);
